import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
    FileText, Plus, CheckCircle, Copy, Trash2, ChevronLeft, ChevronRight,
    Pencil, Save, X, Loader2, ChevronDown, ChevronUp, AlertCircle, Zap
} from "lucide-react";

const DIMENSION_KEYS = [
    "ego_learning", "authority_feedback", "ownership",
    "cognitive_flexibility", "behavioral_followthrough", "reflection_depth"
];

const OPTION_TYPES = ["Defensive", "Avoidant", "Accepting", "Growth-Oriented"];

/* ─── Version List ─── */
function VersionList({ versions, loading, onSelect, onClone, onActivate, onDelete, onNew }) {
    return (
        <div className="p-6 sm:p-8" data-testid="versions-list">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>Assessment Versions</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage assessment question sets and scoring configurations</p>
                </div>
                <Button data-testid="create-version-btn" onClick={onNew} className="gap-2">
                    <Plus className="h-4 w-4" /> New Version
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : versions.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">No versions found.</div>
            ) : (
                <div className="space-y-3">
                    {versions.map(v => (
                        <div key={v.version_id} data-testid={`version-card-${v.version_id}`}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${v.is_active ? "border-[#4A7AB5]/40 bg-[#4A7AB5]/5" : "border-border bg-card hover:border-[#4A7AB5]/20"}`}
                            onClick={() => onSelect(v.version_id)}>
                            <div className="w-10 h-10 rounded-lg bg-[#4A7AB5]/10 flex items-center justify-center flex-shrink-0">
                                <FileText className="h-5 w-5 text-[#4A7AB5]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm text-foreground truncate" style={{ fontFamily: "Outfit" }}>{v.name}</span>
                                    {v.is_active && (
                                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[0.65rem] font-bold uppercase tracking-wider">
                                            <CheckCircle className="h-3 w-3" /> Active
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{v.description || "No description"}</p>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-shrink-0">
                                <span>{v.question_count || 0} questions</span>
                                <span>{v.assessment_count || 0} assessments</span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                {!v.is_active && (
                                    <Button data-testid={`activate-${v.version_id}`} variant="ghost" size="sm" title="Set as active"
                                        onClick={() => onActivate(v.version_id)}>
                                        <Zap className="h-4 w-4 text-amber-500" />
                                    </Button>
                                )}
                                <Button data-testid={`clone-${v.version_id}`} variant="ghost" size="sm" title="Clone"
                                    onClick={() => onClone(v.version_id, v.name)}>
                                    <Copy className="h-4 w-4" />
                                </Button>
                                {!v.is_active && (v.assessment_count || 0) === 0 && (
                                    <Button data-testid={`delete-${v.version_id}`} variant="ghost" size="sm" title="Delete"
                                        onClick={() => onDelete(v.version_id)}>
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                )}
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Question Editor Row ─── */
function QuestionEditor({ question, index, dimensions, onChange, onRemove }) {
    const [expanded, setExpanded] = useState(false);

    const updateField = (field, value) => {
        onChange({ ...question, [field]: value });
    };

    const updateOption = (key, field, value) => {
        const opts = { ...question.options };
        opts[key] = { ...opts[key], [field]: value };
        onChange({ ...question, options: opts });
    };

    return (
        <div className="border border-border rounded-lg bg-card" data-testid={`question-editor-${question.id}`}>
            <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <span className="w-8 h-8 rounded-lg bg-[#4A7AB5]/10 flex items-center justify-center text-xs font-bold text-[#4A7AB5] flex-shrink-0">
                    {question.id}
                </span>
                <p className="flex-1 text-sm text-foreground truncate">{question.text || "New question..."}</p>
                <select className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                    value={question.dimension} onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); updateField("dimension", e.target.value); }}>
                    {dimensions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); onRemove(); }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
                {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </div>
            {expanded && (
                <div className="px-3 pb-4 space-y-3 border-t border-border pt-3">
                    <textarea data-testid={`q-${question.id}-text`}
                        className="w-full text-sm border border-border rounded-lg p-3 bg-background text-foreground resize-none"
                        rows={2} value={question.text} placeholder="Question text..."
                        onChange={e => updateField("text", e.target.value)} />
                    <div className="grid gap-3">
                        {["A", "B", "C", "D"].map(key => (
                            <div key={key} className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/50">
                                <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                                    key === "A" ? "bg-red-100 text-red-700" : key === "B" ? "bg-orange-100 text-orange-700" : key === "C" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                                }`}>{key}</span>
                                <div className="flex-1 space-y-1.5">
                                    <input data-testid={`q-${question.id}-opt-${key}-text`}
                                        className="w-full text-xs border border-border rounded px-2.5 py-1.5 bg-background text-foreground"
                                        value={question.options[key]?.text || ""} placeholder="Option text..."
                                        onChange={e => updateOption(key, "text", e.target.value)} />
                                    <div className="flex gap-2">
                                        <select className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                                            value={question.options[key]?.type || ""} onChange={e => updateOption(key, "type", e.target.value)}>
                                            {OPTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <input className="w-20 text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                                            type="text" value={question.options[key]?.construct || ""} placeholder="Construct..."
                                            onChange={e => updateOption(key, "construct", e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Version Detail ─── */
function VersionDetail({ versionId, onBack }) {
    const [version, setVersion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingMeta, setEditingMeta] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [questions, setQuestions] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/versions/${versionId}`);
            setVersion(data);
            setName(data.name);
            setDescription(data.description || "");
            setQuestions(data.questions || []);
        } catch (e) {
            toast.error("Failed to load version");
        } finally {
            setLoading(false);
        }
    }, [versionId]);

    useEffect(() => { load(); }, [load]);

    const dimensionKeys = version ? Object.keys(version.dimensions || {}) : DIMENSION_KEYS;

    const handleSave = async () => {
        setSaving(true);
        try {
            // Recalculate dimension question mappings from questions
            const dims = { ...(version?.dimensions || {}) };
            for (const dk of Object.keys(dims)) {
                dims[dk] = { ...dims[dk], questions: [] };
            }
            questions.forEach(q => {
                if (dims[q.dimension]) {
                    dims[q.dimension].questions.push(q.id);
                }
            });
            // Recalculate max_score per dimension (4 points max per question)
            for (const dk of Object.keys(dims)) {
                dims[dk].max_score = dims[dk].questions.length * 4;
            }

            await api.put(`/admin/versions/${versionId}`, {
                name, description, questions,
                dimensions: dims,
            });
            toast.success("Version saved");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const addQuestion = () => {
        const nextId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1;
        const defaultOpts = {};
        ["A", "B", "C", "D"].forEach((key, i) => {
            defaultOpts[key] = { text: "", score: [0, 1, 2, 4][i], type: OPTION_TYPES[i], construct: "" };
        });
        setQuestions([...questions, {
            id: nextId, dimension: dimensionKeys[0] || "ego_learning",
            text: "", options: defaultOpts
        }]);
    };

    const removeQuestion = (idx) => {
        setQuestions(questions.filter((_, i) => i !== idx));
    };

    const updateQuestion = (idx, updated) => {
        const copy = [...questions];
        copy[idx] = updated;
        setQuestions(copy);
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
    );

    if (!version) return <div className="p-8 text-center text-muted-foreground">Version not found.</div>;

    return (
        <div className="p-6 sm:p-8" data-testid="version-detail">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <Button data-testid="back-to-versions" variant="ghost" size="sm" onClick={onBack}>
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 min-w-0">
                    {editingMeta ? (
                        <div className="space-y-2">
                            <input data-testid="version-name-input" className="w-full text-lg font-semibold border border-border rounded-lg px-3 py-2 bg-background text-foreground"
                                value={name} onChange={e => setName(e.target.value)} style={{ fontFamily: "Outfit" }} />
                            <input data-testid="version-desc-input" className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-background text-muted-foreground"
                                value={description} onChange={e => setDescription(e.target.value)} placeholder="Description..." />
                            <Button size="sm" variant="ghost" onClick={() => setEditingMeta(false)}>
                                <X className="h-3.5 w-3.5 mr-1" /> Done
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <div>
                                <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate" style={{ fontFamily: "Outfit" }}>{name}</h1>
                                <p className="text-xs text-muted-foreground truncate">{description || "No description"}</p>
                            </div>
                            {version.is_active && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[0.65rem] font-bold uppercase tracking-wider flex-shrink-0">
                                    <CheckCircle className="h-3 w-3" /> Active
                                </span>
                            )}
                            <Button data-testid="edit-meta-btn" variant="ghost" size="sm" onClick={() => setEditingMeta(true)} className="flex-shrink-0">
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    )}
                </div>
                <Button data-testid="save-version-btn" onClick={handleSave} disabled={saving} className="gap-2 flex-shrink-0">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save
                </Button>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 mb-6 text-xs text-muted-foreground">
                <span>{questions.length} questions</span>
                <span>{dimensionKeys.length} dimensions</span>
                <span>ID: {version.version_id}</span>
            </div>

            {/* Questions */}
            <div className="space-y-2 mb-6">
                {questions.map((q, i) => (
                    <QuestionEditor key={`${q.id}-${i}`} question={q} index={i}
                        dimensions={dimensionKeys}
                        onChange={updated => updateQuestion(i, updated)}
                        onRemove={() => removeQuestion(i)} />
                ))}
            </div>

            <Button data-testid="add-question-btn" variant="outline" onClick={addQuestion} className="w-full gap-2">
                <Plus className="h-4 w-4" /> Add Question
            </Button>
        </div>
    );
}

/* ─── Main Page ─── */
export default function AssessmentVersions() {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVersion, setSelectedVersion] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/versions");
            setVersions(data);
        } catch (e) {
            toast.error("Failed to load versions");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleClone = async (fromId, fromName) => {
        try {
            const { data } = await api.post("/admin/versions", {
                clone_from: fromId,
                name: `${fromName} (Copy)`,
                description: `Cloned from ${fromId}`,
            });
            toast.success(`Cloned as ${data.version_id}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Clone failed");
        }
    };

    const handleActivate = async (id) => {
        try {
            await api.put(`/admin/versions/${id}/activate`);
            toast.success("Version activated");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Activation failed");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this version? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/versions/${id}`);
            toast.success("Version deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const handleNew = async () => {
        try {
            const { data } = await api.post("/admin/versions", {
                name: "New Assessment Version",
                description: "Blank version — add questions to get started.",
            });
            toast.success(`Created ${data.version_id}`);
            setSelectedVersion(data.version_id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Creation failed");
        }
    };

    if (selectedVersion) {
        return (
            <VersionDetail
                versionId={selectedVersion}
                onBack={() => { setSelectedVersion(null); load(); }}
            />
        );
    }

    return (
        <VersionList
            versions={versions}
            loading={loading}
            onSelect={setSelectedVersion}
            onClone={handleClone}
            onActivate={handleActivate}
            onDelete={handleDelete}
            onNew={handleNew}
        />
    );
}
